import { Listing, User, SiteContent, DynamicPage, ListingStatus, AnalyticsEventType, AnalyticsEvent, DiscountCode, ChatThread, ChatMessage } from '../types';

const API_BASE = '/backend/api.php?path=';
const AUTH_TOKEN_KEY = 'tbiz_auth_token';

const DEFAULT_AVATAR = 'https://tbiz.ir/profile.png';

function getAuthToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
}

function normalizeListingStatus(rawStatus: any): ListingStatus {
    const raw = String(rawStatus ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');

    if (!raw) return 'pending';

    if (['approved', 'publish', 'published', 'active', 'live'].includes(raw)) {
        return 'approved';
    }

    if (['rejected', 'reject', 'declined', 'failed_review'].includes(raw)) {
        return 'rejected';
    }

    if (['payment_pending', 'pending_payment', 'awaiting_payment', 'unpaid', 'payment_required'].includes(raw)) {
        return 'payment_pending';
    }

    if (['pending', 'pending_approval', 'awaiting_approval', 'under_review', 'submitted', 'draft'].includes(raw)) {
        return 'pending';
    }

    return 'pending';
}

function normalizeListing(raw: any): Listing {
    return {
        ...raw,
        price: toNumber(raw.price, 0),
        trl: toNumber(raw.trl, 0),
        views: toNumber(raw.views, 0),
        status: normalizeListingStatus(raw.status ?? raw.listing_status ?? raw.state),
        ownerId: raw.ownerId ?? raw.owner_id ?? '',
        imageUrls: raw.imageUrls ?? raw.image_urls ?? [],
        videoUrls: raw.videoUrls ?? raw.video_urls ?? [],
        teamMembers: raw.teamMembers ?? raw.team_members ?? [],
        createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
        invoiceNum: raw.invoiceNum ?? raw.invoice_num,
        invoiceDate: raw.invoiceDate ?? raw.invoice_date,
    } as Listing;
}

function toNumber(value: any, fallback = 0): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function normalizePricing(raw: any): any {
    const baseFee = toNumber(raw?.baseFee ?? raw?.base_fee, 300000);
    const brokerageFeeRaw = raw?.brokerageFee ?? raw?.brokerage_fee;
    const brokerageFee = brokerageFeeRaw == null ? undefined : toNumber(brokerageFeeRaw, baseFee);
    const servicesRaw = Array.isArray(raw?.services) ? raw.services : [];
    const services = servicesRaw.map((s: any, idx: number) => ({
        id: String(s?.id ?? idx + 1),
        title: String(s?.title ?? ''),
        description: String(s?.description ?? ''),
        price: toNumber(s?.price, 0),
        icon: s?.icon ?? 'Star',
        applicableTypes: Array.isArray(s?.applicableTypes ?? s?.applicable_types) ? (s?.applicableTypes ?? s?.applicable_types) : [],
        brokerageScope: String(s?.brokerageScope ?? s?.brokerage_scope ?? 'all'),
    }));
    return { baseFee, brokerageFee, services };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE}${path}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...options?.headers,
        },
    });

    const result = await response.json();

    if (!response.ok) {
        const errorMessage = result.error || `API Error: ${response.status} ${response.statusText}`;
        console.error('Backend Error:', result);
        throw new Error(errorMessage);
    }

    return result;
}

export const api = {
    // User & Auth
    getCurrentUser: (): User | null => {
        if (!getAuthToken()) return null;
        const stored = localStorage.getItem('tbiz_current_user');
        return stored ? JSON.parse(stored) : null;
    },

    sendOtp: async (phone: string): Promise<{ success: boolean; message?: string; error?: string }> => {
        return apiFetch<{ success: boolean; message?: string; error?: string }>('users/send-otp', {
            method: 'POST',
            body: JSON.stringify({ phone })
        });
    },

    login: async (identifier: string, otp: string, method: 'phone' | 'google'): Promise<User | null> => {
        const result = await apiFetch<{ success: boolean; user: User; token?: string; error?: string }>('users/login', {
            method: 'POST',
            body: JSON.stringify({ phone: identifier, otp, method })
        });

        if (result.success) {
            localStorage.setItem('tbiz_current_user', JSON.stringify(result.user));
            if (result.token) {
                localStorage.setItem(AUTH_TOKEN_KEY, result.token);
            }
            return result.user;
        }
        return null;
    },

    logout: () => {
        localStorage.removeItem('tbiz_current_user');
        localStorage.removeItem(AUTH_TOKEN_KEY);
    },

    updateUser: async (id: string, data: Partial<User>): Promise<User | null> => {
        const result = await apiFetch<{ success: boolean }>('users/profile', {
            method: 'PUT',
            body: JSON.stringify({ id, ...data })
        });
        if (result.success) {
            const currentUser = api.getCurrentUser();
            if (currentUser && currentUser.id === id) {
                const updated = { ...currentUser, ...data };
                localStorage.setItem('tbiz_current_user', JSON.stringify(updated));
                return updated;
            }
        }
        return null;
    },

    getAllUsers: async (): Promise<User[]> => {
        return apiFetch<User[]>('users');
    },

    // Listings
    getListings: async (): Promise<Listing[]> => {
        const rows = await apiFetch<any[]>('listings');
        return rows.map(normalizeListing);
    },

    getAdminListings: async (): Promise<Listing[]> => {
        const rows = await apiFetch<any[]>('listings&status=all');
        return rows.map(normalizeListing);
    },

    getMyListings: async (ownerId: string): Promise<Listing[]> => {
        const rows = await apiFetch<any[]>(`listings&ownerId=${encodeURIComponent(ownerId)}`);
        return rows.map(normalizeListing);
    },

    getListingById: async (id: string): Promise<Listing | undefined> => {
        try {
            const row = await apiFetch<any>(`listings&id=${id}`);
            return normalizeListing(row);
        } catch {
            return undefined;
        }
    },

    createListing: async (listingData: Omit<Listing, 'id' | 'status' | 'createdAt' | 'views'>): Promise<Listing> => {
        const result = await apiFetch<{ success: boolean; id: string }>('listings', {
            method: 'POST',
            body: JSON.stringify(listingData)
        });
        return {
            ...listingData,
            id: result.id,
            status: 'pending',
            createdAt: new Date().toISOString(),
            views: 0
        } as Listing;
    },

    updateListingStatus: async (id: string, status: ListingStatus): Promise<void> => {
        await apiFetch<{ success: boolean }>('listings', {
            method: 'PUT',
            body: JSON.stringify({ id, status })
        });
    },

    updateListing: async (id: string, listingData: Partial<Listing>): Promise<Listing | null> => {
        const result = await apiFetch<{ success: boolean }>('listings', {
            method: 'PUT',
            body: JSON.stringify({ id, ...listingData })
        });
        if (result.success) {
            return { id, ...listingData } as Listing;
        }
        return null;
    },

    deleteListing: async (id: string): Promise<void> => {
        await apiFetch<{ success: boolean }>(`listings&id=${id}`, {
            method: 'DELETE'
        });
    },

    // Content & CMS
    getPageContent: async (): Promise<SiteContent> => {
        return apiFetch<SiteContent>('site-content');
    },

    updatePageContent: async (content: SiteContent): Promise<void> => {
        await apiFetch<{ success: boolean }>('site-content', {
            method: 'POST',
            body: JSON.stringify(content)
        });
    },

    // Dynamic Pages
    getAllPages: async (): Promise<DynamicPage[]> => {
        return apiFetch<DynamicPage[]>('dynamic-pages');
    },

    getPageBySlug: async (slug: string): Promise<DynamicPage | undefined> => {
        try {
            return await apiFetch<DynamicPage>(`dynamic-pages&slug=${slug}`);
        } catch {
            return undefined;
        }
    },

    savePage: async (page: DynamicPage): Promise<void> => {
        await apiFetch<{ success: boolean }>('dynamic-pages', {
            method: 'POST',
            body: JSON.stringify(page)
        });
    },

    deletePage: async (id: string): Promise<void> => {
        await apiFetch<{ success: boolean }>(`dynamic-pages&id=${id}`, {
            method: 'DELETE'
        });
    },

    uploadFiles: async (files: File[]): Promise<string[]> => {
        const formData = new FormData();
        const token = getAuthToken();
        files.forEach(file => {
            formData.append('files[]', file);
        });

        const response = await fetch(`${API_BASE}upload`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            body: formData,
        });

        let result: any = null;
        try {
            result = await response.json();
        } catch {
            result = null;
        }

        if (!response.ok) {
            const msg = result?.error || response.statusText || `HTTP ${response.status}`;
            throw new Error(`Upload failed: ${msg}`);
        }

        return result.urls || [];
    },

    getTopDonors: async (listingId: string): Promise<any[]> => {
        // Mocking donors for now
        return [
            { userName: 'علی محمدی', units: 50, totalAmount: 50000000, levelLabel: 'حامی طلایی' },
            { userName: 'مریم رضایی', units: 30, totalAmount: 30000000, levelLabel: 'حامی نقره‌ای' },
        ];
    },

    createTransaction: async (data: any): Promise<any> => {
        return apiFetch<any>('payments/request', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    getPricing: async (): Promise<any> => {
        const cfg = await apiFetch<any>('pricing');
        return normalizePricing(cfg);
    },

    validateDiscountCode: async (code: string, context?: { listingType?: string; brokerageType?: string }): Promise<any | null> => {
        const normalized = code.trim();
        if (!normalized) return null;
        try {
            const qs = new URLSearchParams({ code: normalized });
            if (context?.listingType) qs.set('listingType', context.listingType);
            if (context?.brokerageType) qs.set('brokerageType', context.brokerageType);
            const result = await apiFetch<{ success: boolean; discount?: any }>(`discounts/validate&${qs.toString()}`);
            if (result.success && result.discount) {
                return result.discount;
            }
            return null;
        } catch {
            return null;
        }
    },

    updatePricing: async (config: any): Promise<void> => {
        await apiFetch<{ success: boolean }>('pricing', {
            method: 'POST',
            body: JSON.stringify(config)
        });
    },

    // Analytics (Mock tracking for now, but could be sent to backend)
    trackEvent: async (type: AnalyticsEventType, targetId?: string, targetTitle?: string, metadata?: any) => {
        console.log('Tracking Event:', { type, targetId, targetTitle, metadata });
    },

    getAnalytics: async (): Promise<AnalyticsEvent[]> => {
        return apiFetch<AnalyticsEvent[]>('analytics');
    },

    // Discounts
    getDiscountCodes: async (): Promise<DiscountCode[]> => {
        return apiFetch<DiscountCode[]>('discounts');
    },

    createDiscountCode: async (data: any): Promise<void> => {
        await apiFetch<{ success: boolean }>('discounts', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },

    toggleDiscountCode: async (id: string, isActive: boolean): Promise<void> => {
        await apiFetch<{ success: boolean }>('discounts', {
            method: 'PUT',
            body: JSON.stringify({ id, isActive })
        });
    },

    updateDiscountCodeScope: async (id: string, patch: { brokerageScope?: string; applicableTypes?: string[] }): Promise<void> => {
        await apiFetch<{ success: boolean }>('discounts', {
            method: 'PUT',
            body: JSON.stringify({ id, ...patch })
        });
    },

    deleteDiscountCode: async (id: string): Promise<void> => {
        await apiFetch<{ success: boolean }>(`discounts&id=${id}`, {
            method: 'DELETE'
        });
    },

    // Transactions
    getUserTransactions: async (userId: string): Promise<any[]> => {
        return apiFetch<any[]>(`transactions&userId=${userId}`);
    },
    deleteTransaction: async (id: string): Promise<void> => {
        await apiFetch<{ success: boolean }>(`transactions&id=${id}`, {
            method: 'DELETE'
        });
    },

    // Chat
    startChat: async (listingId: string): Promise<ChatThread> => {
        const result = await apiFetch<{ success: boolean; thread: ChatThread }>('chats/start', {
            method: 'POST',
            body: JSON.stringify({ listingId })
        });
        return result.thread;
    },

    getChatThreads: async (): Promise<ChatThread[]> => {
        const result = await apiFetch<{ success: boolean; threads: ChatThread[] }>('chats/threads');
        return result.threads || [];
    },

    getChatMessages: async (threadId: string): Promise<ChatMessage[]> => {
        const result = await apiFetch<{ success: boolean; messages: ChatMessage[] }>(`chats/messages&threadId=${encodeURIComponent(threadId)}`);
        return result.messages || [];
    },

    sendChatMessage: async (threadId: string, message: string): Promise<ChatMessage> => {
        const result = await apiFetch<{ success: boolean; message: ChatMessage }>('chats/messages', {
            method: 'POST',
            body: JSON.stringify({ threadId, message })
        });
        return result.message;
    },

    // Reports
    getUserInvestmentReports: async (userId: string): Promise<any[]> => {
        return apiFetch<any[]>(`reports&userId=${userId}`);
    },

    createReport: async (reportData: any): Promise<void> => {
        await apiFetch<{ success: boolean }>('reports', {
            method: 'POST',
            body: JSON.stringify(reportData)
        });
    },

    // Placeholder for other missing methods to avoid TS errors
    getSystemStats: async () => {
        const listings = await api.getListings();
        const approvedListings = listings.filter(l => l.status === 'approved');
        return {
            totalUsers: 0,
            totalListings: approvedListings.length,
            activeOffers: 0,
            totalMarketValue: approvedListings.reduce((sum, l) => sum + toNumber(l.price, 0), 0)
        };
    },

    resetData: async (): Promise<void> => {
        await apiFetch<{ success: boolean }>('debug/reset', {
            method: 'POST'
        });
        window.location.reload();
    }
};
